'use client';

import { motion } from 'motion/react';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia, VideoMediaHandle } from '@/components/VideoMedia';
import type { Case, Media, MediaBlock } from '@/payload-types';
import { cn } from '@/utils/cn';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './CaseCard.module.scss';

type CaseCardProps = Case & {
  activeSlide?: boolean;
  className?: string;
  disableParallax?: boolean;
  aspectRatio?: NonNullable<MediaBlock['medias']>[number]['aspectRatio'];
  list?: boolean;
  slider?: boolean;
  tracking?: {
    type: string;
    headline: string;
    cta: string;
  };
};

export const CaseCard: React.FC<CaseCardProps> = (props) => {
  const {
    activeSlide,
    aspectRatio,
    card,
    className,
    customer,
    hero,
    slider = false,
    title,
    tracking,
    url,
  } = props;

  const videoRef = useRef<VideoMediaHandle>(null);
  const [motionComplete, setMotionComplete] = useState(false);

  const handleMouseEnter = () => {
    if (slider && !activeSlide) {
      videoRef.current?.play();
    }
  };

  const handleMouseLeave = () => {
    if (slider && !activeSlide) {
      videoRef.current?.pause();
    }
  };

  // When activeSlide, ensure video plays
  useEffect(() => {
    if (videoRef.current && activeSlide) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide, videoRef.current]);

  const MotionLink = useMemo(() => motion.create(Link), []);

  const {
    image: heroImage,
    mediaType: heroMediaType,
    videoEmbedUrl: heroVideoEmbedUrl,
    videoPoster: heroVideoPoster,
  } = hero;

  const {
    image: cardImage,
    mediaType: cardMediaType,
    override,
    title: cardTitle,
    videoEmbedUrl: cardVideoEmbedUrl,
    videoPoster: cardVideoPoster,
  } = card;

  const titleToUse = (override && cardTitle) || title;

  const MediaComponent =
    override && cardMediaType === 'image' && cardImage ? (
      <ImageMedia
        aspectRatio={aspectRatio}
        image={cardImage}
        className={cn(styles.media, {
          [styles.square]: aspectRatio === 'square',
        })}
        responsiveWidths={
          aspectRatio === 'square'
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 1024,
              }
            : aspectRatio === 'portrait'
              ? {
                  mobile: 343,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 343,
                  desktop: 1352,
                  largeDesktop: 2256,
                }
        }
      />
    ) : override && cardMediaType === 'video' && cardVideoEmbedUrl ? (
      <VideoMedia
        ref={videoRef}
        aspectRatio={aspectRatio}
        videoPoster={cardVideoPoster as Media}
        videoEmbedUrl={cardVideoEmbedUrl}
        className={cn(styles.media, {
          [styles.square]: aspectRatio === 'square',
        })}
        videoPlayerClassName={cn(
          {
            [styles.videoPlayer]: aspectRatio === 'square',
          },
          {
            [styles.videoPlayerMobile]: !slider,
          }
        )}
        videoIsBackground={true}
        videoLoops={true}
        playOnHover={slider ? true : false}
        hidePlayPause={activeSlide ? false : slider ? true : false}
        delayed={!motionComplete}
      />
    ) : heroMediaType === 'image' && heroImage ? (
      <ImageMedia
        aspectRatio={aspectRatio}
        image={heroImage}
        className={cn(styles.media, {
          [styles.square]: aspectRatio === 'square',
        })}
        responsiveWidths={
          aspectRatio === 'square'
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 1024,
              }
            : aspectRatio === 'portrait'
              ? {
                  mobile: 343,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 343,
                  desktop: 1352,
                  largeDesktop: 2256,
                }
        }
      />
    ) : heroMediaType === 'video' && heroVideoEmbedUrl ? (
      <VideoMedia
        ref={videoRef}
        aspectRatio={aspectRatio}
        videoPoster={heroVideoPoster as Media}
        videoEmbedUrl={heroVideoEmbedUrl}
        className={cn(styles.media, {
          [styles.square]: aspectRatio === 'square',
        })}
        videoPlayerClassName={cn({
          [styles.videoPlayer]: aspectRatio === 'square',
        })}
        videoIsBackground={true}
        videoLoops={true}
        playOnHover={slider ? true : false}
        hidePlayPause={activeSlide ? false : slider ? true : false}
        delayed={!motionComplete}
      />
    ) : null;

  const handleClick = () => {
    if (tracking) {
      pushGTMEvent({
        event: 'component_click',
        component_cta: tracking.cta,
        component_headline: tracking.headline,
        component_type: tracking.type,
      });
    }
  };

  return (
    <MotionLink
      href={url as string}
      className={cn(styles.card, className, { [styles.sliderCard]: slider })}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      layout='position'
      onAnimationComplete={() => setMotionComplete(true)}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {MediaComponent}
      <div className={styles.content}>
        {customer && <span className={styles.customer}>{customer}</span>}
        {titleToUse && <span className={styles.title}>{titleToUse}</span>}
      </div>
    </MotionLink>
  );
};
